import Auth from '@aws-amplify/auth';
import { getCurrentEnv } from "@sfly/uig-current-environment-util";
import {
    CONFIG,
    PERSIST_USER_LOGIN_COOKIE_NAME,
    UPDATE_WEAK_PASSWORD_COOKIE_NAME,
    ENCRYPTION_KEY,
    VALID_LANGUAGES_REGX,
    EMAIL_SHORTENING_TYPE,
    PERSISTED_LOGIN_COOKIE_EXPIRY_TIME_SECONDS,
    NON_PERSISTED_LOGIN_COOKIE_EXPIRY_TIME_SECONDS,
    LIFETOUCH_SESSION_EXPIRY_FOR_REDIRECTION,
    LIFETOUCH_SESSION_NAME_FOR_REDIRECTION,
    USER_ACCOUNT_TYPES,
    COGNITO_ERRORS,
    RECAPTCHA_URL
} from './AppConstant';
import createLogger from '@sfly/js-logger';
import queryString from 'query-string';
import { validate } from 'validate.js';
import Bowser from "bowser";
import { getUrlRoot } from '@sfly/uig-frontend-version-util';
import { startAppServerSession } from '../service/AppService';
import { OmnitureUtil } from '../util/OmnitureUtil';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import { v4 as uuidv4 } from 'uuid';
import { getStore } from './ReduxUtil';
import jwt_decode from "jwt-decode";
import { parseError } from '../util/CognitoError';
import fetchIntercept from 'fetch-intercept';
import DOMPurify from 'dompurify';
import axios from 'axios';

const queryStringParams = queryString.parse(window.location.search);
const bowser = Bowser.getParser(window.navigator.userAgent);

const COGNITO_SIGN_OUT_RETRY_TIMEOUT = 500;
const COGNITO_SIGN_OUT_RETRY_ATTEMPT = 3;

let stringBundleHost = null; //used to cache calls to getStringBundleHost

/**
 * Returns the configurations for the environment we are currently running in
 * @returns {*}
 */
export function getCurrentEnvironmentConfig() {
    if (getCurrentEnv() !== 'production' && window.location.host.match(/www\.local\.shutterfly\.com/)) {
        return CONFIG['local'];
    }
    return CONFIG[getCurrentEnv()];
}

/**
 * Helper function for getting the string bundles
 *
 */
export const getStringBundleHost = async () => {
    if (stringBundleHost) {
        return stringBundleHost;
    } else {
        // getUrlRoot latest CI version for a frontend package, the link to cdn.
        let host = await getUrlRoot("sfly-sso-fe");

        // If we are running locally, we load the local files not the one from the CDN and deployed version
        if (getCurrentEnv() !== 'production' && window.location.host.match(/local\.shutterfly\.com/)) {
            host = 'https://www.local.shutterfly.com/sfly-sso-fe'
        }

        // Cache the value...
        stringBundleHost = host;
        return stringBundleHost;
    }
};

/**
 * Helper method to see if the supplied brand value is suppose to be on the migration flow or not. It makes no
 * sense SFLY can make it through the migration flow to a SFLY brand. Mostly used for LT and future migrations.
 *
 * @param brand
 * @returns {boolean}
 */
export function isMigratableBrand(brand) {
    if (brand) {
        return ['mlt', 'prestige', 'preschools', 'ltsmiles', 'community', 'mycollection'].indexOf(brand.toLowerCase()) !== -1;
    }
    return false;
}

export function useSflyOmnitureDTL(theme) {
    switch(theme){
        case 'mlt':
        case 'preschools':
        case 'prestige':
        case 'community':
        case 'mycollection':    
            return false;
        default:
            return true;
    }
}

/**
 * Helper function for getting the sso action name
 * USER-4764: Migration flows should respect the "ssoAction=true" param from clients and append signin, signup, etc. to the redirectUri
 * @param {string} page 
 */
export function getSsoActionParam(page) {
    let action;
    switch (page) {
        case 'Migrate':
        case 'SignIn':
        case 'Elevate':
        case 'PresentAndMerged':
        case 'PresentInBoth':
            action = 'signin';
            break;

        case 'SignUp':
        case 'SflyOnly':
        case 'AbsentInBoth':
            action = 'signup';
            break;

        case 'Profile':
            action = 'profilechange';
            break;

        default:
            action = page;
    }
    return action ? action.toLowerCase() : null;
}

export function redirectToClient(redirectURI, page, promo, promo2, clockDrift = '', refreshToken, staySignedIn, wyngPowered, wyngsupport) {
    if (queryStringParams.popupFormSubmittedSuccessfully) return;
    if (redirectURI) {
        const regx = /^(http:\/\/|https:\/\/|\/)/i
        if (!regx.test(redirectURI)) {
            redirectURI = `//${redirectURI}`;
        }

    } else {
        switch (page) {
            case 'SignIn': {
                redirectURI = getCurrentEnvironmentConfig().wwwBaseUrl + '/myaccount/';
                break;
            }
            case 'Migrate':
            case 'Elevate':
            case 'ChangePassword':
            case 'Profile':
            case 'ProfileForceLogin':
            case 'SflyOnly':
            case 'AbsentInBoth':
            case 'PresentAndMerged':
            case 'PresentInBoth':
            case 'SwitchAccount':
            case 'UnlinkAccount':
                redirectURI = getCurrentEnvironmentConfig().wwwBaseUrl + '/myaccount/';
                break;

            case 'SignUp':
                redirectURI = getCurrentEnvironmentConfig().wwwBaseUrl + '/signupconfirmation/';
                break;

            case 'Logout':
            case 'TemporaryResetPassword':
                let queryParams = queryString.parse(window.location.search);
                queryParams = {...queryParams, signedOut: 'true'};
                redirectURI = `${getCurrentEnvironmentConfig().ssoBaseUrl}/?${queryString.stringify(queryParams)}`;
                break;

            default:
                break;
        }
    }

    if (promo && promo.length > 0) {
        // const promoCode = `promoCode=${promo}`;
        const promoCode = wyngsupport && wyngPowered === 'true' ? `promoCode=${promo}&wyngPowered=true&bypassWyng=true` : `promoCode=${promo}`;
        redirectURI = `${redirectURI}${redirectURI.indexOf('?') === -1 ? '?' : '&'}${promoCode}`;
    } else if (wyngsupport && wyngPowered === 'true'){
        redirectURI = `${redirectURI}${redirectURI.indexOf('?') === -1 ? '?' : '&'}wyngPowered=true`;
    }

    if (promo2 && promo2.length > 0) {
        const promoCode2 = `promoCode2=${promo2}`;
        redirectURI = `${redirectURI}${redirectURI.indexOf('?') === -1 ? '?' : '&'}${promoCode2}`;
    }

    // If client pass ssoAction=true to us, we return the last action performed upon redirecting back.
    if (queryStringParams.ssoAction === 'true' && page) {
        const ssoAction = `ssoAction=${getSsoActionParam(page)}`;
        redirectURI = `${redirectURI}${redirectURI.indexOf('?') === -1 ? '?' : '&'}${ssoAction}`;
    }

    if (queryStringParams.popup) {
        const newRedirectUrl = new URL(window.location.href);
        newRedirectUrl.searchParams.set('popupFormSubmittedSuccessfully', 'true');
        redirectURI = newRedirectUrl.toString();
    }
    
    /**
     * !!! ALERT: THIS GETS CRAZY HERE !!!
     * https://jira.corp.shutterfly.com/browse/USER-5216|USER-5215|USER-3913
     *
     * To support many domains and cookies security across domains, we have to bounce the user through the server and the domains we integrate with
     * to set and/or remove cookies in each domain, otherwise our stuff doesn't work in cross domain situations under more secure browsers.
     *
     */
    // Create a form to post to a URL
    const bounceForm = document.createElement('form');
    bounceForm.setAttribute('method', 'POST');
    document.body.appendChild(bounceForm);
    // Set redirectUri for all cases
    const bounceFormRedirectUri = document.createElement('input');
    bounceFormRedirectUri.setAttribute('type', 'hidden');
    bounceFormRedirectUri.setAttribute('name', 'redirectUri');
    bounceFormRedirectUri.setAttribute('value', redirectURI);
    bounceForm.appendChild(bounceFormRedirectUri);

    // Do specific stuff based on the page name
    if(['Logout', 'ChangePassword', 'ProfileForceLogin', 'SwitchAccount', 'UnlinkAccount'].indexOf(page) > -1) {
        // Set the form action to logout
        bounceForm.setAttribute('action', `${getCurrentEnvironmentConfig().ssoBaseUrl}/sso/brand/SFLY/sync/signout`);
        eraseUpdateWeakPasswordCookie();

        // Submit the form to post to the server
        bounceForm.submit();
        return;
    } else if (refreshToken) {
        // Set the form action to sync
        bounceForm.setAttribute('action', `${getCurrentEnvironmentConfig().ssoBaseUrl}/sso/brand/SFLY/sync`);
        const bounceFormRefreshToken = document.createElement('input');
        bounceFormRefreshToken.setAttribute('type', 'hidden');
        bounceFormRefreshToken.setAttribute('name', 'refreshToken');
        bounceFormRefreshToken.setAttribute('value', refreshToken.token);
        bounceForm.appendChild(bounceFormRefreshToken);

          // Set clockDrift for all cases
        const bounceFormClockDrift= document.createElement('input');
        bounceFormClockDrift.setAttribute('type', 'hidden');
        bounceFormClockDrift.setAttribute('name', 'clockDrift');
        bounceFormClockDrift.setAttribute('value', clockDrift);
        bounceForm.appendChild(bounceFormClockDrift);
        
        const bounceFormMaxAgeInSeconds = document.createElement('input');
        bounceFormMaxAgeInSeconds.setAttribute('type', 'hidden');
        bounceFormMaxAgeInSeconds.setAttribute('name', 'maxAgeInSeconds');
        bounceFormMaxAgeInSeconds.setAttribute('value', getPersistCookieTimeInSeconds(staySignedIn));
        bounceForm.appendChild(bounceFormMaxAgeInSeconds);

        // Submit the form to post to the server
        bounceForm.submit();
         return;
    }

    // Places we don't have a token we will just redirect to the client the old fashioned way.
    window.location.assign(redirectURI);
}

export function getPersistCookieTimeInSeconds(staySignedIn) {
    if(staySignedIn) {
        return PERSISTED_LOGIN_COOKIE_EXPIRY_TIME_SECONDS;
    } else {
        return NON_PERSISTED_LOGIN_COOKIE_EXPIRY_TIME_SECONDS;
    }
}

export function setCookie(cookieName, cookieVal, exp, domain) {
    const expires = 'expires=' + exp.toUTCString();
    document.cookie = cookieName + '=' + cookieVal + ';' + expires + ';domain=' + domain + ';path=/';
}


// Sets a cookie to let other apps know the stay signed in checkbox was selected (legacy behavior)
export function setPersistUserLoginCookie(persist) {
    const d = new Date();
    const cookieValue = persist ? 'on' : 'off';
    d.setTime(d.getTime() + (getPersistCookieTimeInSeconds(persist) * 1000));
    setCookie(PERSIST_USER_LOGIN_COOKIE_NAME, cookieValue, d, getCurrentEnvironmentConfig().cookieDomain);
}

// Sets a cookie to let other apps know whether user logged in with a weak password and nag the user to update it.
export function setUpdateWeakPasswordCookie() {
    const d = new Date();
    d.setTime(d.getTime() + (7 *24 * 3600 * 1000));
    setCookie(UPDATE_WEAK_PASSWORD_COOKIE_NAME, 'true', d, getCurrentEnvironmentConfig().cookieDomain);
}

export function eraseUpdateWeakPasswordCookie() {
    eraseCookie(UPDATE_WEAK_PASSWORD_COOKIE_NAME, getCurrentEnvironmentConfig().cookieDomain);
}

/**
 * Get a cookie (use to check feature flag cookie)
 * @param cookieName
 * @returns {*}
 */
export function getCookieValue(cookieName) {
    const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
    if (match) {
        return match[2];
    }
    return null;
}

export function eraseCookie(cookieName, domain) {
    const d = new Date();
    d.setTime(0);
    setCookie(cookieName, '', d, domain);
}

export function parseCookie(cookie) {
    const list = {};
    if (cookie) {
        cookie.split(';').forEach((cookie) => {
            const parts = cookie.split('=');
            list[parts.shift().trim()] = decodeURI(parts.join('='));
        });
    }
    return list;
}

export const translateErrorCode = (translator, code) => {
    if (translator && code) {
        return translator.translate(code, {fallback: code});
    }
    return code;
}

/**
 * logger setup
 */
const defaultData = {
    resource: `${getCurrentEnvironmentConfig().wwwBaseUrl}/application/log.gif`,
    appName: 'sfly-sso-fe',
};

// Ignore eslint error for Webpack.DefinePlugin environment variables
// eslint-disable-next-line no-undef
if (process.env.CI_BUILD_UUID) {
    // eslint-disable-next-line no-undef
    defaultData.appVersion = process.env.CI_BUILD_UUID;
} else {
    defaultData.appVersion = 'local-dev-version';
}

export const logger = createLogger({ name: 'sfly-sso', additionalLogData: defaultData, enableNRD: true, batchInterval: 1000, batchSize: 10 });

/**
 * Determine the current operating system in which the app is running.
 * @returns {String}
 */
export function getOperatingSystem() {
    return bowser.getOS().name;
}

/**
 * Returns true only if it is a mobile OS.
 * @returns {boolean}
 */
export function isMobileOs() {
    return bowser.getPlatformType() === 'mobile';
}

export function isDesktopOs() {
    return bowser.getPlatformType() === 'desktop';
}

export function objectToQueryParams(queryParamObject) {
    return Object.keys(queryParamObject).map(key => key + '=' + queryParamObject[key]).join('&');
}

export function buildRedirectPath(queryParamObject) {
    let url = '/';
    let paramString = objectToQueryParams(queryParamObject);
    if (paramString) {
        url += '?' + paramString;
    }
    return url;
}

/**
 * Deletes cookies set by native signin in social linking, the cookies/tokens are set by '/oauth2/tokens' call implicitly made by amplify. 
 */
export function deleteNativeCookies() {
    // Get an array of all cookie names (the regex matches what we don't want)
    let cookieNames = document.cookie.split(/=[^;]*(?:;\s*|$)/);

    // Remove any that match the pattern
    for (let i = 0; i < cookieNames.length; i++) {
        if (/^CognitoIdentityServiceProvider./.test(cookieNames[i])) {
            document.cookie = cookieNames[i] + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + window.location.hostname;
        }
    }
}

export function deleteCognitoTokensExceptNeededForRefreshTokenAuth() {
    let cookieNames = document.cookie.split(/=[^;]*(?:;\s*|$)/);
    for (let i = 0; i < cookieNames.length; i++) {
        if (/^CognitoIdentityServiceProvider./.test(cookieNames[i]) && !(cookieNames[i].indexOf('refreshToken') > 0) 
                  && !(cookieNames[i].indexOf('LastAuthUser') > 0)  && !(cookieNames[i].indexOf('idToken') > 0)) {
            document.cookie = cookieNames[i] + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + window.location.hostname;
        }
    }
}

/**
 * Set PersistUserLogin cookie, track social signin and start session for social signin
 * @param {*} dtlPageName 
 * @param {*} session 
 * @param {*} requestType 
 * @param {*} persistUser 
 * @param {*} redirectUri
 */
export const setSocialUserPersistCookieAndSession = async (dtlPageName, session, requestType, persistUser, redirectUri) => {
    const staySignedIn = persistUser ? persistUser : window.localStorage.getItem('staySignedIn') === 'true';
    OmnitureUtil.trackSigninSignupConfirmation(dtlPageName, {
        signInSource: 'shutterfly',
        persistSignin: staySignedIn ? 'yes' : 'no',
    });

    // Save the cookiestore
    setPersistUserLoginCookie(staySignedIn);
    // Fire and forget to the token to the app server. We might want to retry like the client does.
    const payload = { requestType: requestType, optIn: staySignedIn };
    const error = await startAppServerSession(session.refreshToken.token, payload, null, redirectUri);
    if (error) {
        logger.error(
            {
                appContext: {
                    msg: 'handleSocialSignin: social signIn, appServer exception',
                    email: session.idToken.payload.email,
                    error: JSON.stringify(error.errorObject),
                }
            });
        OmnitureUtil.trackSigninSignupFailure({
            displayMode: dtlPageName,
            errorMessage: error.message,
        });
    }
    window.localStorage.removeItem('staySignedIn');
    return error;
};

/**
 * Load title and logo as per the brands
 * @param {*} translator 
 * @param {*} pageTitleBrandKey
 */
export function setupPageTitleAndLogo(translator, pageTitleBrandKey) {
    document.title = translator.translate(pageTitleBrandKey, { companyName: translator.translate('company.name') });
    let logoLink = document.querySelectorAll('[rel="shortcut icon"]')[0];
    if (logoLink) {
        logoLink.href = translator.translate('company.title_logo');
    }
}

/**
 * Decrypt URL params received through resetPassword route
 * @param {*} encryptedParams 
 */
export function decryptURLParams(encryptedParams) {
    if (encryptedParams) {
        let decryptedData = AES.decrypt(encryptedParams, ENCRYPTION_KEY).toString(Utf8);
        let result = {};
        decryptedData = decryptedData.split('=');
        result[decryptedData[0]] = decodeURIComponent(decryptedData[1] || '');
        return result;
    }
    return null;
}

/**
 * Custom validator for the confirmPassword and confirmEmail fields.
 * 
 * @Input()
 * value - The value exactly how it looks in the attribute object.
 * options - The options for the validator. Guaranteed to not be null or undefined.
 * key - The attribute name.
 * attributes - The entire attributes object.
 */
validate.validators.confirmFields = (value, options, key, attributes) => {
    if (attributes[options.attributeToMatch] == null && value != null) {
        return null;
    } else if (attributes[options.attributeToMatch] == null) {
        return "^";
    } else if (attributes[options.attributeToMatch] !== null && value == null) {
        return options.message1;
    } else if (attributes[options.attributeToMatch] !== value) {
        return options.message2;
    } else {
        return null;
    }
};

export function getLocalVisitorId() {
    let env = getCurrentEnv();
    if (env && env !== 'production') {
        env = '_' + env;
    } else {
        env = '';
    }
    let visitorId = getCookieValue(`visitor${env}`) || '';
    return visitorId.replace(/['"]+/g, '');
}

export function snooze(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    })
}

export function callAndRetryOnError(cb, retryCount, retryTimeoutInMilliseconds, retryOnErrorCodesArray) {
    return cb()
        .catch((exception) => {
            if (retryCount > 0) {
                if (retryOnErrorCodesArray === null || retryOnErrorCodesArray === undefined || (retryOnErrorCodesArray && retryOnErrorCodesArray.indexOf(exception.response.status) !== -1)) {
                    return snooze(retryTimeoutInMilliseconds).then(() => callAndRetryOnError(cb, retryCount - 1, retryTimeoutInMilliseconds, retryOnErrorCodesArray));
                }
            }
            throw (exception);
        });
}

const fetchAPI = window.fetch;

export function logCognitoError(error, data) {
    const sfly_transactionid = data?.client_metadata_sfly_transactionid;
    // Custom fetch api to add custom request headers to Log api.
    // customFetchAPI function accepts custom headers object which needs to set
    if (sfly_transactionid) {
        window.fetch = customFetchAPI({
            // "sfly-transactionid": sfly_transactionid,
            "txid": sfly_transactionid,
        });
    }

    if (error) {
         data.error = JSON.stringify(error.exception);
       // data.error = error.exception?.message ? error.exception.message : JSON.stringify(error.exception);
        if (error.type === 'metric') {
            logger.info({
                appContext: {
                    errorCode: error.dashboardCode,
                    errorMsg: error.exception?.message,
                    data
                }
            });
        } else {
            logger.error(
                {
                    appContext: {
                        errorCode: error.dashboardCode,
                        errorMsg: error.exception?.message,
                        data
                    }

                });
        }
    }
}

function customFetchAPI(customHeaders = {}) {
    return async function (url, options = undefined) {
        // Set request headers only for Log api's
        if (options && url.includes("beacon")) {
            if (!options.headers) {
                options.headers = {};
            }
            options.headers = {
                ...options.headers,
                ...customHeaders
            };
        }
        try {
            const res = await fetchAPI(url, options);
            return Promise.resolve(res);
        } catch (error) {
            return Promise.reject(error);
        }
    }
};

/**
 * Utility function to check the DOM for potentially bad URLs and logs them if we find them
 */
export function checkForBadCdnUrls() {
    // USER-3514 - Verify we are not using urls in PROD that we should not be using. (src/whitelist.js)
    if (window.checkWhiteListedUrls) {
        const offendingUrls = window.checkWhiteListedUrls();
        if (offendingUrls) {
            logger.error({
                appContext: {
                    msg: 'SSO_WHITE_LISTED_ERROR',
                    offendingUrls,
                }
            })                               
        }
    }
}

/**
 * Helper function to load CSS files
 * @param url
 * @param onload
 * @param onerror
 */
export function loadCss(url, onload, onerror) {
    const cssLink = document.createElement('link');
    cssLink.setAttribute('rel', 'stylesheet');
    cssLink.setAttribute('type', 'text/css');
    cssLink.setAttribute('href', url);
    cssLink.onload = onload;
    cssLink.onerror = onerror;
    document.head.appendChild(cssLink);
}

/**
 * Revoking all auth tokens
 * Revoking all auth tokens
 * Note: although the tokens are revoked, the AWS credentials will remain
 * valid until they expire (which by default is 1 hour)
 */
export function signOutGlobally() {
    const signout = () => {
        return Auth.signOut({ global: true });
    };
    return callAndRetryOnError(signout, COGNITO_SIGN_OUT_RETRY_ATTEMPT,
        COGNITO_SIGN_OUT_RETRY_TIMEOUT).then(() => {
            erasePersistUserLoginCookie();
        });
}

export function erasePersistUserLoginCookie() {
    eraseCookie(PERSIST_USER_LOGIN_COOKIE_NAME, getCurrentEnvironmentConfig().cookieDomain);
}


/**
 * Remove empty string or invalid key. FormAction set validationErors to
 * null when input field passed validation and the translator will throw
 * exception when it see invalid key.
 * @param {*} errors
 */
export function cleanupValidationErrors(errors) {
    if (!errors) {
        return null;
    }
    for (let key of Object.keys(errors)) {
        if (!errors[key]) {
            delete errors[key];
        } else if (Array.isArray(errors[key])) {
            // Remove empty string
            errors[key] = errors[key].filter((msg) => {
                return msg !== '';
            });
            if (errors[key].length === 0) {
                delete errors[key];
            }
        }
    }
    if (Object.keys(errors).length === 0) {
        errors = null;
    }
    return errors;
}

export function isInternetExplorer() {
    return bowser.getBrowserName() === 'Internet Explorer';
}

/**
 * Clear app session after changing password by loading /signout/signout.sfly inside of iframe
 * @param {*} redirectURI
 * @param {*} page
 */
export function clearAppSession(redirectUri, page) {
    var sessionUrl = `${getCurrentEnvironmentConfig().wwwBaseUrl}/signout/ssoSignout.sfly`;
    var iframe = document.createElement('iframe');
    iframe.id = 'clearSessionIframe';
    iframe.src = sessionUrl;
    iframe.classList.add('hidden');
    iframe.onload = function () {
        var iFrame = document.getElementById('clearSessionIframe');
        // Skip iframe document title check in IE 11. See USER-5077 for details.
        if (iFrame && !isInternetExplorer() && sessionUrl.match(/\.shutterfly\.com/) !== null) {
            // we can get the reference to the iframe document if the origin is same
            logger.info({
                appContext: {
                    msg: 'iFrame loaded to clear session of shutterfly.com user'
                }
            });

            let iframeDocument = iFrame.contentDocument;
            if (iframeDocument && iframeDocument.title) {
                let documentTitle = iframeDocument.title;
                if (documentTitle.indexOf("Page not found") >= 0) {
                    logger.error({
                        appContext: {
                            msg: '404 - Page not found error at the time of clearing app session'
                        }
                    });
                }
            }
        }
        if(page !== 'CostcoMembership')
            redirectToClient(redirectUri, page, null, null, null, null, null);
    };
    document.body.appendChild(iframe);
}

/**
 * Helper method, we only keep the tokens needed for RefreshTokenAuth from cookie storage i.e. refreshToken, idToken, LastAuthUser
 * And ask for currentAuthenticatedUser, this will actually update the tokens with recently updated info.
 * @param {*} redirectURI
 * @param {*} page
 */
export const refreshAuthTokensAndRedirectToClient = (redirectURI, page, persistSignin) => {
    Auth.currentAuthenticatedUser().then((result) => {
        redirectToClient(redirectURI, page,  null, null, result.signInUserSession.clockDrift, result.signInUserSession.refreshToken, persistSignin);
    }).catch((error) => {
        logger.error({
                appContext: {
                    msg: 'Unexpected error in refreshing the tokens',
                    error: JSON.stringify(error)
                }
            });
    });
};

export const redirectToSignInWithRedirectUri = (redirectUri) => {
    let queryParams = queryString.parse(window.location.search);
    if (queryParams && !queryParams.redirectUri) {
        queryParams.redirectUri = redirectUri;
    }
    window.location.assign( '/?' + queryString.stringify(queryParams))
};

export const logoutWithRedirectUri = (redirectUri) => {
    // Call the amplify signOut method to delete the tokens
    Auth.signOut()
        .then(() => {
            clearAppSession(redirectUri, 'SwitchAccount');
        })
        .catch((error) => {
            logger.error({
                appContext: {
                    msg: 'Unexpected error in signing out.',
                    error: JSON.stringify(error)
                }
            });
        })
};

/**
 * Function to generate random uuid from uuid library
 */
export const getTransactionId = () => {
    return uuidv4();
};

export const modifyLocationSearch = (locationSearchString, name, value) => {
    const searchString = locationSearchString.substring(1); // remove leading '?' symbol
    const splitted = searchString.split('&');

    let found = false;
    for (let i = 0; i < splitted.length; i++) {
        const item = splitted[i];
        const keyValue = item.trim().split('=');
        if (keyValue.length === 2 && keyValue[0] === name) {
            splitted[i] = `${name}=${value}`;
            found = true;
            break;
        }
    }
    if (!found) {
        splitted.push(`${name}=${value}`);
    }
    return `?${splitted.join('&')}`;
}

/**
 * On reset of password redirect user to redirectUri else redirect to default SFLY signin.
 */
export const redirectToSigninOnPasswordReset = () => {
    let queryParams = queryString.parse(window.location.search);
    let url = '/';

    if (queryParams.redirectUri) {
        url = queryParams.redirectUri;
        let parsedURL = new URL(url);

        // If redirectURI contains queryString
        if(parsedURL.search) {
            try {
                if (parsedURL.search.includes('fromAmazon=true')) {
                    url = queryParams.redirectUri.split('?')[0] + '?' + queryString.stringify(queryString.parse(parsedURL.search));
                }
                else {
                    url= queryParams.redirectUri.split('?')[0] + '?' + queryString.stringify(queryString.parse(parsedURL.search)) + '&passwordChanged=true';
                }
            } catch(e) {
                url+= '?passwordChanged=true';
            }
        } else {
            url+= '?passwordChanged=true'
        }
    } else {
        url += '?' + queryString.stringify(queryParams) + '&passwordChanged=true';
    }
    
    window.location.assign(url);
}

/**
 * Log a message to splunk if required URL param is missing.
 */
export function logMissingUrlParams(parsedParams, requiredParams) {
    if (!requiredParams)
        return;
    const parsed = parsedParams || {}, missing = [];
    requiredParams.forEach((required) => {
        if (!parsed[required]) {
            missing.push(required);
        } else if (required === 'email') {
            const errors = validate.single(parsed[required], {
                email: {
                    email: { message: "^errors" }
                }});
            if (errors) {
                missing.push(required);
            }
        }
    });
    missing.length && logger.warn({
        appContext: {
            msg: 'MISSING_URL_PARAM',
            url: window.location.href,
            referrer: document.referrer,
            param: missing.join(',')
        }
    });
}

export function shouldLogMissingUrlParams(url) {
    if (url.match(/www\.local\.shutterfly|:9090|\/logout|signedOut=/)) {
        return false;
    }
    return url.match(/accounts.shutterfly|accounts.stage.shutterfly/) !== null;
}

/**
 * Returns the suuport language key like 'en', 'es', or 'fr'. Returns 'en' if a match is not found.
 * @returns {string}
 */
export function getNavigatorLangauge() {
    const match = window.navigator.language.match(VALID_LANGUAGES_REGX);
    return match ? match[0] : 'en';
}

export function shortenString(str, charsToShow, replacement) {
    if (str && str.length > charsToShow * 2) {
        return `${str.slice(0, charsToShow)}${replacement || ''}${str.slice(-charsToShow)}`;
    }
    return str;
}

/**
 * Shorten email address based on length. See USER-4991 for details.
 * 
 * @param {*} email 
 * @param {*} type 
 */
export function shortenEmail(email, type) {
    email = DOMPurify.sanitize(email);
    if (!email) {
        return email;
    }
    let charsToShow = 3;
    const symbol = (type === EMAIL_SHORTENING_TYPE.MASK ? '***' : '...');
    const [username, domain] = email.split('@');
    const len = username.length;
    if (type === EMAIL_SHORTENING_TYPE.BUTTON) {
        if (len < 12) {
            return email;
        }
    } else {
        // paragraph
        if (len < 6) {
            charsToShow = 1;
        } else if (len < 10) {
            charsToShow = 2;
        }
    }
    return [shortenString(username, charsToShow, symbol), domain].join('@');
}

/**
 *
 * @param {*} emailId
 */
export const signoutAndReloadPage = (emailId) => {
    // Call the amplify signOut method to delete the tokens
    Auth.signOut()
        .then(() => {
            window.location.reload();
        })
        .catch((error) => {
            logger.error({
                appContext: {
                    msg: 'Unexpected error in signing out.',
                    email: emailId,
                    error: JSON.stringify(error)
                }
            });
        })
};

export function getClientMetadata(brand, theme) {
    const dontSendWelcomeEmailList = ['lti'];
    const transactionId = getTransactionId();
    const clientMetadata = {
        'brand': brand,
        'theme': theme,
        'sfly-application-name': 'sflyssofe',
        'logged_in_device': window.navigator.userAgent,
        'sfly-transactionid': transactionId,
        'is_test_account': 'false',
        'is_internal_account': 'false',
        'send_welcome_email': dontSendWelcomeEmailList.indexOf(theme) !== -1 ? 'false' : 'true',
        'welcome_email_brand_id': theme === 'lti' ? 'LTI' : brand,
        'domain_origin': window.location.hostname.indexOf('local') > -1 ? 'accounts.dev.shutterfly.com' : window.location.hostname,
    };
    return clientMetadata;
}

/**
 * Redirect user to change account page or defaults to the URL we use for the primary brand logo
 * @param {*} redirectUri 
 * @param {*} translator 
 */
export const redirectToChangeAccountPage = (redirectUri, translator) => {
    if (!redirectUri) {
        /**
         * TODO: Make this environment specific like `company.link_logo_1${getCurrentEnv() === 'production' ? '' : '.'+ getCurrentEnv()}
         * to produce something like company.link_logo_1.dev, company.link_logo_1.kappa, etc.
         * See https://github.com/sflyinc-shutterfly/ui-guild/tree/master/packages/uig-current-environment-util#getcurrentenv
         */
        redirectUri = translator.translate(`company.link_logo_1${getCurrentEnv() === 'production' ? '' : `_${getCurrentEnv()}`}`);
    }
    window.location.assign(redirectUri);
};

/**
 * This function treat brand as SFLY if theme is lti
 */
export const getBrandIdentifierByTheme = () => {
    const { brand, theme } = getStore().getState().app;
    switch(theme) {
        case 'lti':
            return 'SFLY';
        default:
            return brand;
    }
}

/**
 * Saves email Id and current timestamp to localstorage to maintain the lifetouch session for sometime.
 * @param {String} email 
 * 
 */
export const saveLifetouchSession = (email, brandSignInToken) => {
    var currentTime = new Date();
    const timestamp = (new Date()).getTime();
    const cookieValue = JSON.stringify({email, timestamp, brandSignInToken});
    currentTime.setTime(currentTime.getTime() + LIFETOUCH_SESSION_EXPIRY_FOR_REDIRECTION);
    setCookie(LIFETOUCH_SESSION_NAME_FOR_REDIRECTION, cookieValue, currentTime,  getCurrentEnvironmentConfig().cookieDomain);
}

export const removeLifetouchSession = () => {
    eraseCookie(LIFETOUCH_SESSION_NAME_FOR_REDIRECTION, getCurrentEnvironmentConfig().cookieDomain);
}

export const getSavedLifetouchSession = (email) => {
    var lifetouchSession =  getCookieValue(LIFETOUCH_SESSION_NAME_FOR_REDIRECTION); 
    lifetouchSession = lifetouchSession ? JSON.parse(lifetouchSession) : {};
    const timeStampDifferene = (new Date()).getTime() - lifetouchSession.timestamp;
    if(email === lifetouchSession.email && timeStampDifferene < LIFETOUCH_SESSION_EXPIRY_FOR_REDIRECTION) { // primary check: check the session exists for the same email id
        return lifetouchSession.brandSignInToken;
    }
    removeLifetouchSession();
    return null;
}

/**
 * Check if user is elevated
 */
export const isElevationNeeded = (authTime, awsConfig) => {
    if(authTime && awsConfig.elevationEnabled && Date.now() - (authTime*1000) > (awsConfig.elevationDuration*1000)) {
        return true;
    }
    return false;
}

export const redirectToSignInWithNewTab = () => {
    window.open('/', '_blank');
}

export const isUserDeniedSocialLogin = (error) => {
    if (error) {
        return error && error.includes('user_denied');
    }
    return false;
}

export const isEmailDeclined = (error) => {
    if (error) {
        return error && error.includes('email')
    }
    return false;
}


export const isLoginFromSocialProviders = (groups = []) => {
    const result = getSocialProviders(groups);
    return result && result.length > 0;
}

export const getProviderName = (groups = []) => {
    const result = getSocialProviderNames(groups);
    return result;
}

const getSocialProviderNames = (groups) => {
    const socialProviders = [];
    groups.forEach(group => {
        group = group.toLowerCase();

        if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.FACEBOOK.toLocaleLowerCase())) {
            socialProviders.push('Facebook');

        } else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.GOOGLE.toLocaleLowerCase())) {
            socialProviders.push('Google');
            
        } else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.APPLE.toLocaleLowerCase())) {
            socialProviders.push('Apple');

        }  else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.AMAZON.toLocaleLowerCase())) {
            socialProviders.push('Amazon');
        }
    
    });

    return socialProviders;
}

const getSocialProviders = (groups) => {
    const socialProviders = [];
    groups.forEach(group => {
        group = group.toLowerCase();

        if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.FACEBOOK.toLocaleLowerCase())) {
            socialProviders.push(USER_ACCOUNT_TYPES.PROVIDERS.FACEBOOK);

        } else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.GOOGLE.toLocaleLowerCase())) {
            socialProviders.push(USER_ACCOUNT_TYPES.PROVIDERS.GOOGLE);
            
        } else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.APPLE.toLocaleLowerCase())) {
            socialProviders.push(USER_ACCOUNT_TYPES.PROVIDERS.APPLE);

        } else if(group.includes(USER_ACCOUNT_TYPES.PROVIDERS.AMAZON.toLocaleLowerCase())) {
            socialProviders.push(USER_ACCOUNT_TYPES.PROVIDERS.AMAZON);
        }
    
    });

    return socialProviders;
}

/**
 * @param {*} groups 
 */
export const getLoggedInUserType = (groups = []) => {
    const userInfo = {
        status: USER_ACCOUNT_TYPES.NATIVE,
        socialProviders: []
    };

    // check if this is true all the time
    const isCognitoUser = (groups.filter( (group) => { 
                                return USER_ACCOUNT_TYPES.COGNITO_GROUPS.includes(group.toLowerCase());
                            })).length > 0;

    // get list of social providers
    const socialProviders = getSocialProviders(groups);

    // we have 2 variables and 2 flags so we are checking status of users
    if(isCognitoUser && socialProviders.length > 0) {
        userInfo.status = USER_ACCOUNT_TYPES.LINKED;

    } else if(isCognitoUser && socialProviders.length === 0) {
        userInfo.status = USER_ACCOUNT_TYPES.NATIVE;

    } else if(!isCognitoUser && socialProviders.length > 0){
        userInfo.status = USER_ACCOUNT_TYPES.SOCIAL_ONLY;

    }

    userInfo.socialProviders = socialProviders;

    return userInfo;
}

export const getURLOrigin = () => {
    const origin = DOMPurify.sanitize(window.location.origin);
    if(origin.match(/local.shutterfly/)) {
        return origin + '/sfly-sso-fe';
    }

    return origin;
}


export const getSflyIfFromToken = (token) => {
    if(token){
        return jwt_decode(token);
    }
}

/**
 * Disable the events on the page.
 * Especially used on social integration callback urls so users cant force to change the flow
 * @param {boolean} [disableClick=true] Flag to determine whether to disable or enable click events.
*/
export const disableClickOnPage = (disableClick = true) => {
    try {
        document.querySelector('body').style['pointer-events'] = disableClick ? 'none' : '';
    } catch(e) {
        console.log("error disabling the events");
    }
}

export const getPromoCodes = (promocodeCollection, location) => {
        const { promoCodeParam, promoCodeParam2, promoCode, promoCode2 } = promocodeCollection;
        const { state } = location || {};
        // If URL promocode from url does not match with promocodes saved in store that means code has changed from current view.
        const promocodes = {
            promo: promoCode,
            promo2: promoCode2
        };
        if(state) {
            promocodes.promo = (promoCodeParam !== promoCode && promoCode) ? promoCode : state.promoCode;
            promocodes.promo2 = (promoCodeParam2 !== promoCode2 && promoCode2) ? promoCode2 : state.promoCode2;
        }
        return promocodes;
}

export const isInappBrowser = () => {
    let ua = window.navigator.userAgent || window.navigator.vendor || window.opera;
    logger.info({
        appContext: {
            msg: ua,
            'logged_in_device': window.navigator.userAgent
        }
    });
    let isInstagram = (ua.indexOf('Instagram') > -1) ? true : false;
    let isFacebook = ua.indexOf("FBAN") > -1 || ua.indexOf("FBAV") > -1 ? true : false;
    let isTiktok = (ua.indexOf('TikTok') > -1) ? true : false;
    return isInstagram || isFacebook || isTiktok;
}


/**
 * Method to handle sub windows postMessages,
 * accepts a message and a callback to handle a potential error.
 * @param {*} message
 * @param {*} setPageLevelError
 * @param {*} translator
 */
export const handleSubWindowPostMessages = (message, setPageLevelError, translator) => {
    try {
        if (!window.opener) {
            throw new Error('window opener is null');
        }
        const currentEnv = getCurrentEnv();
        const emptyOrEnv = currentEnv === 'production' ? '' : `${currentEnv}.`; 
        const hosts = /shutterfly|tinyprints|lifetouch/;
        const originMatch = window.location.origin.match(hosts);
        if (originMatch && originMatch[0]) {
            const protocol = `${window.location.protocol}//www`;
            const domains = /.com|.net/;
            const currentDomain = window.location.origin.match(domains);
            const domainToUse = currentDomain && currentDomain[0] ? currentDomain[0] : '.com';
            const hostOrigin = `${emptyOrEnv}${originMatch[0]}`;
            const targetOrigin = `${protocol}.${hostOrigin}${domainToUse}/`;
            window.opener.postMessage(message, targetOrigin);
            window.opener.focus();
        }
    } catch (error) {
        if (setPageLevelError && typeof setPageLevelError === 'function') {
            const msToAutoClose = 9000;
            const ERROR_CODE = 'SignInNoRedirectPostMessageFailed';
            const cognitoError = parseError(error, 'SignInPopUp');
            const correctErrorCode = cognitoError.error === COGNITO_ERRORS[ERROR_CODE];
            const translatedOrDefaultMessage = 
                translator && correctErrorCode ? translateErrorCode(translator, cognitoError.error) : 
                'There was a problem communicating with the service, the window will close shortly, please try again later.';
            setPageLevelError(translatedOrDefaultMessage, false);
            setTimeout(() => {
                if (window) window.close();
                if (window.opener) window.opener.focus();
            }, msToAutoClose);
        }
    }
}


/** Method to intercept authenticate post call and add req headers with cookie
 * This triggers only for migration flows because interceptor of sso-client can take care of other flows
 */
export function interceptIamPost(customHeaders) {
    window.unregister = fetchIntercept.register({
        request: function (url, config) {
            // Modify the url or config here
            if (url === Auth.userPool.client.endpoint && config.method === "POST") {
                // config.credentials = 'include';
                config.headers = { ...config.headers, ...customHeaders };
                return [url, config];
            } else {
                // config.headers = config.headers;
                return [url, config];
            }
        }
    });
}

export function appendRecaptchatoDOM() {
    let recaptchaScript = document.createElement("script");
    recaptchaScript.async = true;
    recaptchaScript.defer = true;
    recaptchaScript.setAttribute("src", RECAPTCHA_URL + getCurrentEnvironmentConfig().SITE_KEY );
    document.body.appendChild(recaptchaScript);
    recaptchaScript.onload = () => {
    }
}

export function removeRecaptchaFromDOM() {
    let recaptchaClass = window.parent.document.getElementsByClassName('grecaptcha-badge').item(0);
    if (recaptchaClass)
        recaptchaClass.remove()
}

export async function linkAccount(result, newAccount) {
    const data = {
        "nativeUserToken": result?.idToken?.jwtToken,
        "referenceToken": window.localStorage.getItem("referenceToken"),
        "newAccount": newAccount,
        "amzUserID": window.localStorage.getItem("amzUserId"),
    }
    if (!data.amzUserID || !data.referenceToken) {
        return { isSuccess: false, amzResponse: { response: {} }, isSessionExpired : true };
    }

    const config = {
        headers: {
            'Content-Type': 'application/json',
            "SFLY-apiKey": getCurrentEnvironmentConfig().resetPasswordApigeeApiKey
        }
    }
    try {
        const response = await axios.post(getCurrentEnvironmentConfig().userCloudServiceBaseUrl + '/api/v1/users/amazon/onboarding/linking', data, config);
        console.log("linking API", response);
        window.localStorage.setItem('amzLinkingRes', JSON.stringify(response.data))
        return {isSuccess: true, amzResponse: response.data, isSessionExpired: false };
    } catch (error) {
        console.log("linking API error", error);
        const isSessionExpired = error.response?.data?.message?.includes('token expired');
        window.localStorage.setItem('amzLinkingRes', JSON.stringify(error))
        return {isSuccess: false, amzResponse: error, isSessionExpired };
    }
}

export function appleGoogleBrandsCheck(brand = '', theme = '', appleGoogleBrands = []) {
    brand = brand.toLowerCase();
    theme = theme.toLowerCase();
    appleGoogleBrands = appleGoogleBrands.map(it => it.toLowerCase());

    // means this is LTI page
    if ((brand === 'lti' || theme === 'lti') && appleGoogleBrands.includes('lti')) {
        return true;
    } else {
        return appleGoogleBrands.includes(brand);
    }
}

export function brandUserNativeSignInErrorHandle(translator, exception, theme, brand) {
    let providers = [];
    function checkBrandUser(brand) {
        return ['mlt', 'lti', 'ltsmiles', 'prestige', 'preschools', 'community', 'mycollection', 'inc'].includes(brand?.toLowerCase());
    }
    exception.message.split(" ").forEach((str) => {
        if (['amazon', 'google', 'facebook', 'apple'].includes(str.toLowerCase().replace(/,/g, ''))) {
            if(checkBrandUser(theme) || checkBrandUser(brand)) {
                if(str.toLowerCase() === 'amazon')
                    return;
                else
                    providers.push(str);
            } else {
                providers.push(str);
            }
        } else if ("Forgotpassword" === str) {
            providers = true
        }
    });
    function formatArrayWithAnd(arr) {
        if (arr.length === 0) return '';
        if (arr.length === 1) return arr[0];
        let allButLast = arr.slice(0, -1).join(', ');
        return `${allButLast} and ${arr[arr.length - 1]}`;
    }
    function isAppleAccount() {
        if(providers.includes("Apple") || providers.includes("Amazon")) {
            return 'errors.cognito.single_brand_user_native_login_apple_error';
        } else {
            return 'errors.cognito.single_brand_user_native_login_error';
        }
    }
    let translated;
    if (providers === true || providers.length === 0)
        translated = translator.translate("errors.cognito.brand_amazon_user_reset_password");
    else
        translated = translator.translate(providers.length === 1 ? isAppleAccount() : 'errors.cognito.brand_user_native_login_error', { provider: formatArrayWithAnd(providers) });
    return translated;
}